//libs
import { createSelector } from 'reselect';
import { RouteComponentProps } from 'react-router';

//types
import { ListingState } from './types';
import { DEFAULT_REQUEST } from './constants';
import { AppState } from 'shared/store/app/types';
import { DEFAULT_STORE } from 'shared/helpers/storeUrl';
import { getDomainConfig } from 'shared/domainConfig/helpers';

export const listing = (state: { listingV2: ListingState }) => state.listingV2;
export const router = (state: { router: RouteComponentProps }) => state.router;
export const app = (state: { app?: AppState }) => state.app;

export const getTotalCount = createSelector([listing], (listingData) => listingData.count);

export const getTitle = createSelector([listing], (listingData) => listingData.title);

export const getProducts = createSelector([listing], (listingData) => listingData.products);

export const getCumulativeOOSCount = createSelector(
  [listing],
  (listingData) => listingData.cumulativeOOSCount || 0
);

export const getFirstTwelveProducts = createSelector([listing], (listingData) =>
  listingData.products
    .filter((product) => {
      return product.type !== 'tiptile';
    })
    .slice(0, 12)
);

export const getFilters = createSelector([listing], (listingData) => listingData.filters);

export const getIsFetching = createSelector([listing], (listingData) => listingData.isFetching);

export const getIsNotFound = createSelector([listing], (listingData) => listingData.isNotFound);

export const getIsFetchingError = createSelector(
  [listing],
  (listingData) => listingData.isFetchingError
);

export const getAppliedFilters = createSelector(
  [listing],
  (listingData) => listingData.appliedFilters
);

export const getPayload = createSelector([listing, app], (listingData, appData) => {
  const storeId = appData?.storeId;
  const payload: Record<string, string | number> = {
    ...listingData.appliedFilters,
    pageNumber: listingData.currentPage,
  };
  const catalogTagPrefix = getDomainConfig().CATALOG_TAG_PREFIX;
  if (storeId && catalogTagPrefix) {
    payload['catalog_tag_filter'] = `${catalogTagPrefix}${storeId}`;
  }
  return payload;
});

export const getPagenumber = createSelector([listing], (listingData) => listingData.currentPage);

export const getBannerImages = createSelector([listing], (listingData) => listingData.bannerImages);

export const getLocation = createSelector([router], (routerData) => routerData.location);

export const getIsInitial = createSelector([listing], (listingData) => listingData.isInitial);

export const getAdPlatformWidgets = createSelector(
  [listing],
  (listingData) => listingData.adPlatformData
);

export const getAdPlatformWidgetsData = createSelector(
  [listing],
  (listingData) => listingData.adPlatformWidgetData.result || []
);
export const getUIMessage = createSelector([listing], (listingData) => listingData.uiMessage);

export const getMetaData = createSelector([listing], (listingData) => listingData.metaData);

export const getIsFetchingWithFilters = createSelector(
  [listing],
  (listingData) => listingData.isFetchingWithFilters
);
export const getFilterWithNames = createSelector(
  [listing],
  (listingData) => listingData.filterWithNames
);
export const getQueryExpansionTerm = createSelector(
  [listing],
  (listingData) => listingData.query_expansion_term
);

export const getQueryExpansionType = createSelector(
  [listing],
  (listingData) => listingData.query_expansion_type
);

export const getIsBrand = createSelector([listing], (listingData) => listingData.isBrand);

export const getImpressiveProducts = createSelector([listing], (listingData) => {
  return (
    listingData.products
      ?.slice(0, Number(DEFAULT_REQUEST.PageSize))
      .map((product) => product.id)
      .join(',') || ''
  );
}); // todo: to remove this tracking from omniture as per nilay

export const getCategoryId = createSelector([listing], (listingData) => listingData.categoryId);
export const getFilterGuides = createSelector([listing], (listingData) => listingData.filterGuides);
export const getFooterHTML = createSelector([listing], (listingData) => listingData.footerHTML);
export const getIsOfferPage = createSelector([listing], (listingData) => listingData.isOfferPage);

export const getMerchandisingDetails = createSelector(
  [listing],
  (listingData) => listingData?.trackingMetaData?.merchandising_details || {}
);

export const getEntityData = createSelector([listing], (listingData) => listingData.entityData);

export const getTrackingMetadata = createSelector(
  [listing, app],
  (listingData, appData) => {
    const trackingMetaData = listingData?.trackingMetaData || {};
    const storeId = appData?.storeId;
    const storeSelected = storeId === DEFAULT_STORE ? 'fashion' : storeId;

    return storeSelected
      ? { ...trackingMetaData, store_selected: storeSelected }
      : trackingMetaData;
  }
);

export const getBreadCrumbs = createSelector(
  [listing],
  (listingData) => listingData.breadcrumbs || []
);

export const getMetaTitle = createSelector(
  [listing],
  (listingData) => listingData?.metaData?.h1_tag || ''
);

export const getStoreRedirect = createSelector(
  [listing],
  (listingData) => listingData.store_redirection || ''
);

export const getStoreRedirectCount = createSelector(
  [listing],
  (listingData) => listingData.storeRedirectCount ?? 0
);

export const getIsSearch = createSelector(
  [listing],
  (listingData) => listingData.isSearch ?? false
);
